/* eslint-disable jira/filename-case */
import React, { useEffect, useState, type ReactNode } from 'react';
import { toProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { usePostOfficeExernalMessageProxy } from '@atlassian/post-office-context';
import { fg } from '@atlassian/jira-feature-gating';
import { useCoordinationClient } from './services/coordination-client/index.tsx';
import { useDeferEpMessageKey } from './services/ep-messages/index.tsx';

type EPCheckProps = {
	onComplete: () => void;
	children: ReactNode;
};

const usePostOfficeCheck = (onComplete: () => void) => {
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const epMessageId = useDeferEpMessageKey(projectType);

	const { hasMessageStarted, requestMessageStart } = usePostOfficeExernalMessageProxy(
		epMessageId ?? 'unknown',
	);

	useEffect(() => {
		if (!fg('defer-edition-ep-migration-message-proxy-gate')) return; // if post office proxy is disabled, don't start message via Post Office Check
		if (hasMessageStarted || projectType === undefined) return;
		requestMessageStart();
	}, [projectType, hasMessageStarted, requestMessageStart]);

	useEffect(() => {
		if (!hasMessageStarted) return;
		onComplete();
	}, [hasMessageStarted, onComplete]);

	return hasMessageStarted;
};

const useEPCheck = (onComplete: () => void) => {
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);

	const [hasMessageStarted, setHasMessageStarted] = useState(false);
	const [hasCheckedEpMessage, setHasCheckedEpMessage] = useState(false);
	const { start: checkEPMessage } = useCoordinationClient(projectType);

	useEffect(() => {
		// reset checks when we switch projects
		setHasCheckedEpMessage(false);
		setHasMessageStarted(false);
	}, [projectType]);

	useEffect(() => {
		if (fg('defer-edition-ep-migration-message-proxy-gate')) return; // if post office proxy is enabled, don't start message via EP Check
		if (hasCheckedEpMessage || projectType === undefined) return;

		(async () => {
			setHasCheckedEpMessage(true);
			const hasStarted = await checkEPMessage();

			setHasMessageStarted(hasStarted);
			if (hasStarted) {
				onComplete();
			}
		})();
	}, [checkEPMessage, hasCheckedEpMessage, onComplete, projectType]);

	return hasMessageStarted;
};

export const EPCheck = ({ onComplete, children }: EPCheckProps): React.JSX.Element => {
	const engagementPlatformResult = useEPCheck(onComplete);
	const postOfficeResult = usePostOfficeCheck(onComplete);

	const hasMessageStarted = fg('defer-edition-ep-migration-message-proxy-gate')
		? postOfficeResult
		: engagementPlatformResult;

	return <>{hasMessageStarted && children}</>;
};
